import { useCallback, useEffect, useRef, useState } from "react";
import { getCloudState, putCloudState, type AccountSession, type CloudStateResponse } from "./auth";
import { normalizeState } from "./domain";
import type { AppState } from "./types";
import type { RoamController } from "./useRoam";
import type { SyncConflict } from "./components/SyncConflictDialog";

export type SyncStatus = "signed_out" | "loading" | "ready" | "saving" | "conflict" | "offline" | "error";

function meaningful(state: AppState): boolean {
  return state.onboardingComplete || state.trips.length > 0 || state.activity.length > 0;
}

function serialized(state: AppState): string {
  return JSON.stringify(state);
}

export function useAccountSync(session: AccountSession | null, controller: RoamController) {
  const [status, setStatus] = useState<SyncStatus>(session ? "loading" : "signed_out");
  const [message, setMessage] = useState(session ? "Connecting account data…" : "Trips are stored in this browser.");
  const [conflict, setConflict] = useState<SyncConflict | null>(null);
  const revisionRef = useRef(0);
  const initializedRef = useRef<string | null>(null);
  const lastSyncedRef = useRef("");
  const currentStateRef = useRef(controller.state);
  const pendingRef = useRef<AppState | null>(null);
  const savingRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  currentStateRef.current = controller.state;

  const applyConflict = useCallback((latest: CloudStateResponse | undefined) => {
    pendingRef.current = null;
    if (!latest?.state) {
      setStatus("error");
      setMessage("Cloud data changed unexpectedly. Sign out and try again.");
      return;
    }
    const next = normalizeState(latest.state);
    setConflict({ state: next, revision: latest.revision, updatedAt: latest.updatedAt });
    setStatus("conflict");
    setMessage("Choose which trip data to keep.");
  }, []);

  const flush = useCallback(async () => {
    if (!session || savingRef.current || !pendingRef.current || conflict) return;
    const snapshot = pendingRef.current;
    pendingRef.current = null;
    const snapshotJson = serialized(snapshot);
    savingRef.current = true;
    setStatus("saving");
    setMessage("Saving trips to your CodePawl account…");
    try {
      const result = await putCloudState(snapshot, revisionRef.current);
      revisionRef.current = result.revision;
      lastSyncedRef.current = snapshotJson;
      setStatus("ready");
      setMessage("Trips are synced to your CodePawl account.");
    } catch (caught) {
      if (caught && typeof caught === "object" && "code" in caught && caught.code === "SYNC_CONFLICT") {
        applyConflict((caught as { latest?: CloudStateResponse }).latest);
      } else {
        pendingRef.current = snapshot;
        setStatus(navigator.onLine ? "error" : "offline");
        setMessage(navigator.onLine ? "Account sync needs attention." : "Offline. Changes remain safe in this browser.");
      }
    } finally {
      savingRef.current = false;
      if (pendingRef.current && !conflict) void flush();
    }
  }, [applyConflict, conflict, session]);

  useEffect(() => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    pendingRef.current = null;
    setConflict(null);
    if (!session) {
      initializedRef.current = null;
      revisionRef.current = 0;
      lastSyncedRef.current = "";
      setStatus("signed_out");
      setMessage("Trips are stored in this browser.");
      return;
    }
    const abort = new AbortController();
    initializedRef.current = null;
    setStatus("loading");
    setMessage("Connecting account data…");
    void getCloudState(abort.signal).then(async (cloud) => {
      if (abort.signal.aborted) return;
      const local = currentStateRef.current;
      if (!cloud.state) {
        const saved = await putCloudState(local, cloud.revision);
        revisionRef.current = saved.revision;
        lastSyncedRef.current = serialized(local);
      } else {
        const remote = normalizeState(cloud.state);
        revisionRef.current = cloud.revision;
        if (meaningful(local) && serialized(local) !== serialized(remote)) {
          setConflict({ state: remote, revision: cloud.revision, updatedAt: cloud.updatedAt });
          setStatus("conflict");
          setMessage("Choose which trip data to keep.");
          initializedRef.current = session.user.id;
          return;
        }
        controller.replaceState(remote);
        lastSyncedRef.current = serialized(remote);
      }
      initializedRef.current = session.user.id;
      setStatus("ready");
      setMessage("Trips are synced to your CodePawl account.");
    }).catch(() => {
      if (!abort.signal.aborted) {
        setStatus(navigator.onLine ? "error" : "offline");
        setMessage(navigator.onLine ? "Account sync could not connect." : "Offline. Changes remain safe in this browser.");
      }
    });
    return () => abort.abort();
  }, [controller.replaceState, session?.user.id]);

  useEffect(() => {
    if (!session || initializedRef.current !== session.user.id || conflict) return;
    const nextJson = serialized(controller.state);
    if (nextJson === lastSyncedRef.current) return;
    pendingRef.current = controller.state;
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => void flush(), 700);
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [conflict, controller.state, flush, session]);

  const useCloud = useCallback(() => {
    if (!conflict) return;
    controller.replaceState(conflict.state);
    revisionRef.current = conflict.revision;
    lastSyncedRef.current = serialized(conflict.state);
    setConflict(null);
    setStatus("ready");
    setMessage("Account data is now active on this browser.");
  }, [conflict, controller]);

  const keepDevice = useCallback(async () => {
    if (!conflict) return;
    setStatus("saving");
    setMessage("Uploading this browser’s trips…");
    try {
      const result = await putCloudState(currentStateRef.current, conflict.revision);
      revisionRef.current = result.revision;
      lastSyncedRef.current = serialized(currentStateRef.current);
      setConflict(null);
      setStatus("ready");
      setMessage("This browser’s trips are now synced.");
    } catch (caught) {
      applyConflict((caught as { latest?: CloudStateResponse }).latest);
    }
  }, [applyConflict, conflict]);

  return { status, message, conflict, useCloud, keepDevice };
}
